<template>
    <div class="event-participant-selector">
        <el-dropdown
            trigger="click"
            placement="bottom"
            @visible-change="_onToggle"
        >
            <div
                class="event-participant-selector__link"
                :class="{
                    'event-participant-selector__link--selected': !!value,
                    'event-participant-selector__link--placeholder': !value
                }"
                :title="selectedParticipantFullname || placeholder"
            >
                <span>{{ selectedParticipantFullname || placeholder }}</span>
                <i
                    v-if="value"
                    class="event-participant-selector__clear el-icon-circle-close"
                    @click.stop="_onClear"
                />
                <i
                    class="event-participant-selector__icon"
                    :class="{'el-icon-arrow-up': isOpened, 'el-icon-arrow-down': !isOpened}"
                />
            </div>
            <el-dropdown-menu
                slot="dropdown"
                class="event-participant-selector-menu"
            >
                <div class="event-participant-selector-menu__search">
                    <el-input
                        ref="searchInput"
                        v-model.trim="search"
                        clearable
                        placeholder="Начните вводить имя гостя"
                        @click.native.stop
                        @input="_onSearchInput"
                    />
                </div>
                <div
                    v-loading="isLoading"
                    class="event-participant-selector-menu__list"
                >
                    <div
                        v-if="!preparedItems.length && search.length >= 2 && !isLoading"
                        class="event-participant-selector-menu__empty"
                    >
                        Ничего не найдено
                    </div>
                    <el-dropdown-item
                        v-for="item in preparedItems"
                        :key="item.id"
                        :command="item"
                        @click.native.prevent="_onParticipantSelect(item)"
                    >
                        <el-row
                            :gutter="24"
                            class="event-participant-selector-item"
                        >
                            <el-col :span="14">
                                <participant-name-with-age
                                    :with-link="false"
                                    :name="item.fullname"
                                    :age="item.age"
                                    class="event-participant-selector__name"
                                />
                            </el-col>
                            <el-col
                                :span="4"
                                align="right"
                                class="event-participant-selector__city"
                            >
                                {{ item.city }}
                            </el-col>
                            <el-col :span="1">
                                <gender-icon :gender="item.gender" />
                            </el-col>
                            <el-col
                                :span="4"
                                align="left"
                                class="event-participant-selector__status"
                            >
                                {{ item.status }}
                            </el-col>
                        </el-row>
                    </el-dropdown-item>
                </div>
            </el-dropdown-menu>
        </el-dropdown>
    </div>
</template>

<script>
import _ from 'lodash';
import { mapState } from 'vuex';
import { Event } from '@/api';
import ParticipantNameWithAge from '@/modules/participant/components/common/ParticipantNameWithAge';
import GenderIcon from '@/components/common/GenderIcon';

export default {
    name: 'EventParticipantSelector',

    components: {
        GenderIcon,
        ParticipantNameWithAge
    },

    props: {
        value: {
            type: Object,
            default: null
        },

        placeholder: {
            type: String,
            default: 'Выберите гостя'
        }
    },

    data() {
        return {
            search: '',
            isOpened: false,
            isLoading: false,
            participants: []
        };
    },

    computed: {
        ...mapState('event', {
            event: 'resource'
        }),

        eventId() {
            return this.$prop('event.id');
        },

        selectedParticipantFullname() {
            return this.$prop('value.attributes.fullname', '');
        },

        preparedItems() {
            return this.participants.map(participant => ({
                id: participant.id,
                fullname: participant.attributes.fullname,
                city: participant.attributes.city,
                status: participant.attributes.status,
                gender: participant.attributes.gender,
                age: participant.attributes.age,
                participant
            }));
        }
    },

    watch: {
        eventId() {
            this._resetSearch();
        }
    },

    methods: {
        _onToggle(isOpened) {
            this.isOpened = isOpened;

            if (isOpened) {
                this.$nextTick(() => {
                    if (this.$refs.searchInput) {
                        this.$refs.searchInput.focus();
                    }
                });
                return;
            }

            this._resetSearch();
        },

        _onSearchInput(value) {
            this._fetchParticipants(value);
        },

        _fetchParticipants: _.debounce(function(search) {
            if (!this.eventId || !search || search.length < 2) {
                this.participants = [];
                this.isLoading = false;
                return;
            }

            this.isLoading = true;

            Event.Participant.list({
                eventId: this.eventId,
                fio: search,
                _with: []
            })
                .then(resource => {
                    this.participants = _.get(resource, 'data', []);
                })
                .catch(() => {
                    this.participants = [];
                })
                .finally(() => {
                    this.isLoading = false;
                });
        }, 300),

        _onParticipantSelect(item) {
            this.$emit('input', item ? item.participant : null);
            this.isOpened = false;
            this._resetSearch();
        },

        _onClear() {
            this.$emit('input', null);
            this._resetSearch();
        },

        _resetSearch() {
            this.search = '';
            this.participants = [];
            this.isLoading = false;
        }
    }
};
</script>

<style lang="scss">
@import "@vars";

.event-participant-selector-menu {
    border-radius: 0;
    border: none;
    padding: 0;
    width: 624px;

    &__search {
        position: sticky;
        top: 0;
        z-index: 1;
        background: #fff;
        padding: 19px 22px 12px;
    }

    &__list {
        max-height: 320px;
        overflow: auto;
        padding: 0 0 19px;
    }

    &__empty {
        padding: 0 22px 12px;
        color: #909399;
    }

    .popper__arrow {
        display: none;
    }

    .el-dropdown-menu__item {
        padding: 0 22px;
        white-space: normal;
    }
}

.event-participant-selector {
    &__status, &__city, &__name {
        overflow: hidden;
        text-overflow: ellipsis;
    }

    &__clear {
        position: absolute;
        top: 50%;
        right: 38px;
        transform: translate3d(0, -50%, 0);
        font-size: 18px;
        color: #c0c4cc;
        cursor: pointer;

        &:hover {
            color: #f56c6c;
        }
    }

    &__icon {
        position: absolute;
        top: 50%;
        right: 10px;
        transform: translate3d(0, -50%, 0);
        font-size: 20px;
        color: #d8d8d8;
    }

    &__link {
        margin: 14px 0 0 6px;
        position: relative;
        border-bottom: 2px solid #eee;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        width: 620px;
        font-size: 18px;
        line-height: 23px;
        color: #000;
        padding: 0 58px 0 10px;
        outline: none;
        cursor: pointer;

        &--selected {
            border-bottom-color: #67c23a;
            box-shadow: inset 0 -1px 0 0 rgba(103, 194, 58, 0.25);
        }

        &--placeholder {
            color: #c0c4cc;
        }
    }
}

.event-participant-selector-item {
    color: #000;
    width: 580px;
    font-size: 14px;
    line-height: 18px;
    padding: 8px 0;
    margin: 0;
}
</style>
